import React, { useState } from 'react';
import RiskIndicator from './RiskIndicator';

/**
 * BoundaryFlagsList - Displays list of boundary flags with actions
 */
const BoundaryFlagsList = ({ flags, onAcknowledge, onResolve, onDismiss, loading }) => {
  const [expandedFlag, setExpandedFlag] = useState(null);
  const [actionNote, setActionNote] = useState('');

  const cardStyle = {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px'
  };

  const headerStyle = {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '16px',
    color: '#333',
    borderBottom: '1px solid #eee',
    paddingBottom: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  const flagItemStyle = {
    padding: '12px',
    marginBottom: '10px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    borderLeft: '4px solid #ccc'
  };

  const getFlagBorderColor = (severity) => {
    const colors = {
      critical: '#dc3545',
      high: '#fd7e14',
      medium: '#ffc107',
      low: '#28a745',
      info: '#17a2b8'
    };
    return colors[severity] || '#ccc';
  };

  const flagTypeLabels = {
    cbc_persistent_drift: 'Persistent Drift',
    cbc_low_therapeutic_engagement: 'Low Therapeutic Engagement',
    cbc_role_abandonment_pattern: 'Role Abandonment',
    cbc_client_resistance_pattern: 'Client Resistance',
    cbc_therapeutic_rupture: 'Therapeutic Rupture',
    cbc_attachment_language_pattern: 'Attachment Language',
    cbc_high_engagement_frequency: 'High Engagement',
    cbc_critical_engagement_frequency: 'Critical Engagement',
    cbc_dependency_escalation: 'Dependency Escalation',
    cbc_social_isolation_indicators: 'Social Isolation',
    cbc_delusional_attribution: 'Delusional Attribution'
  };

  const buttonStyle = {
    padding: '6px 12px',
    fontSize: '12px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    marginRight: '8px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#007bff',
    color: '#fff'
  };

  const successButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#28a745',
    color: '#fff'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#6c757d',
    color: '#fff'
  };

  const handleAction = async (flagId, action) => {
    const callbacks = {
      acknowledge: onAcknowledge,
      resolve: onResolve,
      dismiss: onDismiss
    };

    const callback = callbacks[action];
    if (callback) {
      await callback(flagId, actionNote);
      setExpandedFlag(null);
      setActionNote('');
    }
  };

  if (!flags || flags.length === 0) {
    return (
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span>Active Flags</span>
          <span style={{ fontSize: '12px', color: '#28a745' }}>No active flags</span>
        </div>
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          No boundary concerns flagged for this client.
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span>Active Flags ({flags.length})</span>
      </div>

      {flags.map((flag) => (
        <div
          key={flag.id}
          style={{
            ...flagItemStyle,
            borderLeftColor: getFlagBorderColor(flag.severity)
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <RiskIndicator level={flag.severity} size="small" />
                <strong>{flag.title || flagTypeLabels[flag.type] || flag.type}</strong>
              </div>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#555' }}>
                {flag.description}
              </p>
              {flag.recommendation && (
                <p style={{ margin: '0', fontSize: '12px', color: '#0066cc', fontStyle: 'italic' }}>
                  Recommendation: {flag.recommendation}
                </p>
              )}
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#888' }}>
                Created: {new Date(flag.createdAt).toLocaleDateString()} |
                Status: {flag.status}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ marginTop: '12px', borderTop: '1px solid #e0e0e0', paddingTop: '12px' }}>
            {expandedFlag === flag.id ? (
              <div>
                <textarea
                  placeholder="Add a note (optional)"
                  value={actionNote}
                  onChange={(e) => setActionNote(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    marginBottom: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '12px',
                    resize: 'vertical',
                    minHeight: '60px'
                  }}
                />
                <div>
                  {flag.status === 'active' && (
                    <button
                      style={primaryButtonStyle}
                      onClick={() => handleAction(flag.id, 'acknowledge')}
                      disabled={loading}
                    >
                      Acknowledge
                    </button>
                  )}
                  <button
                    style={successButtonStyle}
                    onClick={() => handleAction(flag.id, 'resolve')}
                    disabled={loading}
                  >
                    Mark Resolved
                  </button>
                  <button
                    style={secondaryButtonStyle}
                    onClick={() => handleAction(flag.id, 'dismiss')}
                    disabled={loading}
                  >
                    Dismiss
                  </button>
                  <button
                    style={{ ...buttonStyle, backgroundColor: '#e0e0e0', color: '#333' }}
                    onClick={() => setExpandedFlag(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                style={{ ...buttonStyle, backgroundColor: '#e9ecef', color: '#333' }}
                onClick={() => setExpandedFlag(flag.id)}
              >
                Take Action
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BoundaryFlagsList;
